import { FullBackgroundPreview } from './FullBackgroundSettings';
import ThemeActions from '../themes/ThemeActions';
import { ImageBackgroundPreview } from './ImageBackgroundSection';
import MonsoonPreview from './MonsoonPreview';
import ImageSectionPreview from './ImageSectionPreview';
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Accordion, Form, Collapse } from 'react-bootstrap';
import { showConfirm } from 'react-confirm-prompt';
import mobSectionApi from '../../../services/mobsectionapi';
import designThemeApi from '../../../services/designThemeApi';
import SectionForm from './SectionForm';
import '../sections.modern.css';
import '../SectionList.css';
import EditItemModal from './EditItemModal';
import Loader from '../../loader/loader';
import BannerEditModal from './BannerEditModal';

const sectionTypeMap = {
    1: 'Banner',
    2: 'Products Card',
    3: 'Recommended Products',
    4: 'Brands',
    5: 'Banner with Two Row Products',
    6: 'Monsoon Offer',
    8: 'Full-background image cards',
    9: 'Image grid with full background'
};

const sectionForMap = {
    1: 'Home Page',
    2: 'Offer Page'
};

function MobileSectionList({ type }) {
    const initialSectionFor = type === 'offer' ? 2 : 1;

    const [themes, setThemes] = useState([]);
    const [themeId, setThemeId] = useState(() => { const id = Number(new URLSearchParams(window.location.search).get('theme_id')); return Number.isSafeInteger(id) && id > 0 ? id : 1; });
    const [sectionFor] = useState(initialSectionFor);
    const [openSections, setOpenSections] = useState({});
    const [sections, setSections] = useState([]);
    const [products, setProducts] = useState([]);

    const [editingSection, setEditingSection] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const [selectedItem, setSelectedItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [saving, setSaving] = useState(false);

    const [loaderVisible, setLoaderVisible] = useState(false);

    const [showBannerEditModal, setShowBannerEditModal] = useState(false);
    const toggleSectionOpen = (id) => {
        setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // --- ordering helpers ---
    const getNum = (v, fallback = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    };

    const sortByOrder = (arr = [], key = 'order_by') => {
        const withIndex = (arr || []).map((x, idx) => ({ ...x, __idx: idx }));
        return withIndex
            .sort((a, b) => {
                const ao = getNum(a[key], a.__idx + 1);
                const bo = getNum(b[key], b.__idx + 1);
                return ao - bo;
            })
            .map(({ __idx, ...rest }) => rest);
    };
    const [bannerEditType, setBannerEditType] = useState('section');

    const activeThemes = useMemo(() => (themes || []).filter((t) => t.status !== 9), [themes]);

    const [creatingRowForSectionId, setCreatingRowForSectionId] = useState(null);
    const [creatingRow, setCreatingRow] = useState(false);


    const buildTempRow = (secId, existingRowsCount) => ({
        id: `tmp_${Date.now()}`,          // temporary id for UI
        mobsecID: secId,
        title: `Row ${existingRowsCount + 1}`,
        noofcols: 1,
        categoryID: 0,
        bannerImg: null,
        url: null,
        status: 1,
        mobdata: []
    });


    const handleAddRow = async (sec) => {
        const ok = await askConfirm(
            "Add a new row?",
            "A new row will be created under this section. You can add items after creating it."
        );
        if (!ok) return;

        // show loader only for this section button
        setCreatingRowForSectionId(sec.id);
        setCreatingRow(true);

        // optimistic row UI (show immediately)
        const tempRow = buildTempRow(sec.id, (sec.mobrow || []).length);
        setSections((prev) =>
            prev.map((s) =>
                s.id === sec.id
                    ? { ...s, mobrow: [...(s.mobrow || []), tempRow], noofrows: (s.noofrows || 0) + 1 }
                    : s
            )
        );

        try {
            const payload = {
                mobsecID: sec.id,
                title: tempRow.title,
                noofcols: 1,
                categoryID: 0,
                status: 1,
            };

            const res = await mobSectionApi.createRow(payload);

            if (!res?.success) {
                // revert optimistic UI if failed
                setSections((prev) =>
                    prev.map((s) =>
                        s.id === sec.id
                            ? {
                                ...s,
                                mobrow: (s.mobrow || []).filter((r) => String(r.id) !== String(tempRow.id)),
                                noofrows: Math.max((s.noofrows || 1) - 1, 0),
                            }
                            : s
                    )
                );

                console.error("Create row failed:", res?.message);
                return;
            }

            // Replace temp row with real row from backend (if backend returns it)
            const createdRow = res.data;
            if (createdRow?.id) {
                setSections((prev) =>
                    prev.map((s) =>
                        s.id === sec.id
                            ? {
                                ...s,
                                mobrow: (s.mobrow || []).map((r) =>
                                    String(r.id) === String(tempRow.id) ? createdRow : r
                                ),
                            }
                            : s
                    )
                );
            }

            // final refresh to ensure section data matches DB
            await fetchSections();
        } catch (e) {
            // revert optimistic UI on exception
            setSections((prev) =>
                prev.map((s) =>
                    s.id === sec.id
                        ? {
                            ...s,
                            mobrow: (s.mobrow || []).filter((r) => String(r.id) !== String(tempRow.id)),
                            noofrows: Math.max((s.noofrows || 1) - 1, 0),
                        }
                        : s
                )
            );
            console.error("Add row error:", e);
        } finally {
            setCreatingRow(false);
            setCreatingRowForSectionId(null);
        }
    };


    const askConfirm = async (title, description) => {
        const ok = await showConfirm(title, { description, type: 'info' });
        return !!ok;
    };


    const fetchThemes = async () => {
        try {
            const res = await designThemeApi.listThemes();
            if (res?.success) setThemes(res.data || []);
        } catch (e) {
            console.error('Theme fetch error', e);
        }
    };

    const fetchSections = async () => {
        try {
            const res = await mobSectionApi.getAllSections({
                type,
                theme_id: themeId,
                section_for: sectionFor
            });

            if (res?.success && Array.isArray(res.data)) {
                setSections(res.data);
                setProducts(res.products || []);
            } else {
                console.error('Invalid response format', res);
                setSections([]);
                setProducts([]);
            }
        } catch (err) {
            console.error('Error fetching sections:', err);
            setSections([]);
            setProducts([]);
        }
    };

    useEffect(() => {
        fetchThemes();
    }, []);

    useEffect(() => {
        fetchSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeId, type]);

    useEffect(() => {
        setOpenSections((prev) => {
            const next = { ...prev };
            (sections || []).forEach((s) => {
                if (next[s.id] === undefined) next[s.id] = true; // default open only first time
            });
            return next;
        });
    }, [sections]);



    const handleAddSection = () => {
        setEditingSection(null);
        setShowForm(true);
    };

    const handleEditSection = (sec) => {
        setEditingSection(sec);
        setShowForm(true);
    };

    const handleDeleteSection = async (sec) => {
        const ok = await askConfirm(
            'Delete this section?',
            'This will remove the section from this theme + page.'
        );
        if (!ok) return;

        setLoaderVisible(true);
        try {
            await mobSectionApi.deleteSection(sec.id);
            await fetchSections();
        } finally {
            setLoaderVisible(false);
        }
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingSection(null);
        fetchSections();
    };

    const handleItemEdit = (item) => {
        setSelectedItem(item);
        setShowEditModal(true);
    };

    const handleAddItem = (rowId, categoryCard = false) => {
        setSelectedItem({
            id: null,
            title: '',
            slug: '',
            url: '',
            actionIdType: categoryCard ? 2 : 1,
            actionID: '',
            status: 1,
            mobsecListID: rowId
        });
        setShowEditModal(true);
    };

    const handleModalClose = () => {
        setShowEditModal(false);
        setSelectedItem(null);
        fetchSections();
    };

    const handleItemSave = async (updatedData) => {
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('title', updatedData.title || '');
            fd.append('slug', updatedData.slug || '');
            fd.append('url', updatedData.url || '');
            fd.append('actionIdType', updatedData.actionIdType);
            fd.append('actionID', updatedData.actionID || '');
            fd.append('status', updatedData.status);

            if (updatedData.files) fd.append('files', updatedData.files);

            let response = null;
            if (updatedData.id) {
                fd.append('id', updatedData.id);
                response = await mobSectionApi.updateItem(fd);
            } else {
                fd.append('mobsecListID', updatedData.mobsecListID);
                response = await mobSectionApi.createItem(fd);
            }

            if (response?.success) {
                setShowEditModal(false);
                setSelectedItem(null);
                fetchSections();
            }
        } catch (err) {
            console.error('Error saving item:', err);
        } finally {
            setSaving(false);
        }
    };

    const getProductDetails = (item) => {
        if (item.actionIdType === 1) {
            const flatProducts = (products || []).flat();
            const product = flatProducts.find((p) => String(p.action_id) === String(item.actionID));
            const itemImage = item.valueData ?? '';
            return product ? (
                <div className="d-flex align-items-center">
                    <img
                        src={
                            itemImage
                                ? process.env.REACT_APP_IMAGE_URL + itemImage
                                : `${process.env.REACT_APP_IMAGE_URL}/uploads/${product.image?.[1]}`
                        }
                        alt={product.productName}
                        style={{ width: 52, height: 52, objectFit: 'cover', marginRight: 10, borderRadius: 10 }}
                    />
                    <div style={{ textAlign: 'left' }}>
                        <div className="fw-semibold">{product.productName || 'Unnamed Product'}</div>
                        <div className="text-muted small">
                            {product.unit} {product.unitName}
                        </div>
                        <div className="text-primary small">{product.brandName}</div>
                    </div>
                </div>
            ) : (
                <span className="text-danger">⚠️ Product not found for actionID: {item.actionID}</span>
            );
        }

        return (
            <>
                <strong>{item.slug || 'Unnamed'}</strong>
                <span className="text-muted ms-2">({item.url})</span>
            </>
        );
    };

    const handleStatusToggle = async (entityType, id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 2 : 1;

        const ok = await askConfirm(
            newStatus === 2 ? 'Deactivate?' : 'Activate?',
            newStatus === 2 ? 'This will hide it from users.' : 'This will make it visible to users.'
        );
        if (!ok) return;

        setLoaderVisible(true);
        try {
            if (entityType === 'mobdata') {
                const formData = new FormData();
                formData.append('id', id);
                formData.append('status', newStatus);
                const response = await mobSectionApi.updateItem(formData);
                if (response?.success) fetchSections();
            } else if (entityType === 'row') {
                const response = await mobSectionApi.updateListStatus({ id, status: newStatus });
                if (response?.success) fetchSections();
            } else if (entityType === 'section') {
                const formData = new FormData();
                formData.append('id', id);
                formData.append('status', newStatus);
                const response = await mobSectionApi.updateSectionData(formData);
                if (response?.success) fetchSections();
            }
        } catch (error) {
            console.error(`Error updating status for ${entityType} ${id}:`, error);
        } finally {
            setLoaderVisible(false);
        }
    };

    const handleEditBanner = (section, editType) => {
        setBannerEditType(editType);
        setEditingSection(section);
        setShowBannerEditModal(true);
    };

    // --- ORDERING ACTIONS (Section / Row / Item) ---
    const moveSection = async (sectionId, direction) => {
        const ordered = sortByOrder(sections, 'order_by');
        const idx = ordered.findIndex((s) => String(s.id) === String(sectionId));
        const swapWith = direction === 'up' ? idx - 1 : idx + 1;
        if (idx < 0 || swapWith < 0 || swapWith >= ordered.length) return;

        const a = ordered[idx];
        const b = ordered[swapWith];

        const aOrder = getNum(a.order_by, idx + 1);
        const bOrder = getNum(b.order_by, swapWith + 1);

        setLoaderVisible(true);
        try {
            await Promise.all([
                mobSectionApi.updateSection({ id: a.id, order_by: bOrder }),
                mobSectionApi.updateSection({ id: b.id, order_by: aOrder }),
            ]);
            await fetchSections();
        } catch (e) {
            console.error('Mobile section reorder error:', e);
        } finally {
            setLoaderVisible(false);
        }
    };

    const moveRow = async (sec, rowId, direction) => {
        const rows = sec?.mobrow || [];
        const orderedRows = sortByOrder(rows, 'orderList');
        const idx = orderedRows.findIndex((r) => String(r.id) === String(rowId));
        const swapWith = direction === 'up' ? idx - 1 : idx + 1;
        if (idx < 0 || swapWith < 0 || swapWith >= orderedRows.length) return;

        const a = orderedRows[idx];
        const b = orderedRows[swapWith];

        const aOrder = getNum(a.orderList, idx + 1);
        const bOrder = getNum(b.orderList, swapWith + 1);

        setLoaderVisible(true);
        try {
            await Promise.all([
                mobSectionApi.updateListStatus({ id: a.id, orderList: bOrder }),
                mobSectionApi.updateListStatus({ id: b.id, orderList: aOrder }),
            ]);
            await fetchSections();
        } catch (e) {
            console.error('Mobile row reorder error:', e);
        } finally {
            setLoaderVisible(false);
        }
    };

    const moveItem = async (row, itemId, direction) => {
        const items = row?.mobdata || [];
        const orderedItems = sortByOrder(items, 'orderList');
        const idx = orderedItems.findIndex((it) => String(it.id) === String(itemId));
        const swapWith = direction === 'up' ? idx - 1 : idx + 1;
        if (idx < 0 || swapWith < 0 || swapWith >= orderedItems.length) return;

        const a = orderedItems[idx];
        const b = orderedItems[swapWith];

        const aOrder = getNum(a.orderList, idx + 1);
        const bOrder = getNum(b.orderList, swapWith + 1);

        setLoaderVisible(true);
        try {
            const fdA = new FormData();
            fdA.append('id', a.id);
            fdA.append('orderList', bOrder);

            const fdB = new FormData();
            fdB.append('id', b.id);
            fdB.append('orderList', aOrder);

            await Promise.all([mobSectionApi.updateItem(fdA), mobSectionApi.updateItem(fdB)]);
            await fetchSections();
        } catch (e) {
            console.error('Mobile item reorder error:', e);
        } finally {
            setLoaderVisible(false);
        }
    };

    const pageTitle = sectionFor === 2 ? 'Mobile Offer Page Sections' : 'Mobile Home Page Sections';

    return (
        <Container className="my-4">
            {/* TOP HEADER */}
            <div className="secPageHeader">
                <div className="secPageTitleWrap">
                    <h2 className="mb-0">{pageTitle}</h2>
                    <div className="secSubtitle">Choose a theme to edit its sections. Create a theme here, then publish it now or schedule it.</div>
                </div>

                <div className="secTopActions">
                    <Form.Select
                        size="sm"
                        value={themeId}
                        onChange={(e) => setThemeId(parseInt(e.target.value))}
                    >
                        {activeThemes.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name} ({t.code})
                            </option>
                        ))}
                    </Form.Select>

                    <ThemeActions themes={activeThemes} themeId={themeId} sectionFor={sectionFor} onCreated={async theme => {
                        await fetchThemes();
                        if (theme?.id) setThemeId(Number(theme.id));
                    }} />
                    <Button size="sm" className=" btnCompactWide" onClick={handleAddSection}>
                        + Create Section
                    </Button>
                </div>
            </div>

            {loaderVisible && <Loader />}

            {sortByOrder(sections, 'order_by').map((sec) => {
                const isOpen = !!openSections[sec.id];

                return (
                    <div key={sec.id} className="secCard mb-4">
                        {/* Small line */}
                        <div className="px-3 py-2 bg-light border-bottom text-muted small">
                            {sectionForMap[sec.sectionFor] || '📄 Unknown Page'} | Theme ID: {sec.themeId || themeId}
                        </div>

                        {/* SECTION HEADER */}
                        <div className="secCardHead"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSectionOpen(sec.id)}>
                            <div className="secCardHeadLeft">
                                <div className="secCardTitle">
                                    {sectionTypeMap[sec.secType] || 'Section'}
                                    <span className={`badge badgePill ${sec.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                        {sec.status === 1 ? 'Active' : 'Inactive'}
                                    </span>

                                    <span className="ms-2 text-muted">
                                        {openSections[sec.id] ? '▾' : '▸'}
                                    </span>

                                    {parseInt(sec.seeAll) === 1 ? (
                                        <span className="badge bg-info ms-2">See All</span>
                                    ) : null}
                                </div>

                                <div className="secMeta">
                                    <div>Order: <b>{getNum(sec.order_by, '-') || '-'}</b></div>
                                    <div>No. of Rows: <b>{sec.noofrows}</b></div>
                                    {sec.title ? <div>Title: <b>{sec.title}</b></div> : null}
                                </div>
                            </div>

                            <div className="secCardHeadRight">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="btnCompact"
                                    title="Move up"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        moveSection(sec.id, 'up');
                                    }}
                                >
                                    ↑
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="btnCompact"
                                    title="Move down"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        moveSection(sec.id, 'down');
                                    }}
                                >
                                    ↓
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="btnCompact btnCompactWide"
                                    disabled={creatingRow && creatingRowForSectionId === sec.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddRow(sec);
                                    }}
                                >
                                    {creatingRow && creatingRowForSectionId === sec.id ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            Adding...
                                        </>
                                    ) : (
                                        "+ Add Row"
                                    )}
                                </Button>

                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="btnCompact btnCompactWide"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSection(sec);
                                    }}
                                >
                                    Edit
                                </Button>

                                <Button
                                    variant={sec.status === 1 ? 'outline-danger' : 'outline-success'}
                                    size="sm"
                                    className="btnCompact"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusToggle('section', sec.id, sec.status);
                                    }}
                                >
                                    {sec.status === 1 ? 'Disable' : 'Enable'}
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="btnCompact"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSection(sec);
                                    }}
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>

                        <Collapse in={isOpen}>
                            <div>
                                {Number(sec.secType) === 8 && <div className="p-3">
                                    <div className="alert alert-info mb-2">Use Edit background for the full section artwork. Add image cards in the rows below; each card keeps its own destination. Edit appearance to choose boxed or image-only cards. Mobile layout version 2 support is required.</div>
                                    <FullBackgroundPreview title={sec.title} config={sec.presentationConfig} background={sec.bannerImg} rows={sec.mobrow} />
                                </div>}
                                {Number(sec.secType) === 9 && <div className="p-3">
                                    <div className="alert alert-info mb-2">Upload one full background through Edit background, then add your item images and links below. Columns follow the active item count in each row. Text and decoration come from your uploaded artwork.</div>
                                    <ImageBackgroundPreview section={sec} />
                                </div>}
                                {Number(sec.secType) === 6 && <div className="p-3">
                                    <MonsoonPreview section={sec} />
                                </div>}
                                {[1, 2].includes(Number(sec.secType)) && <div className="p-3">
                                    <ImageSectionPreview section={sec} />
                                </div>}
                                {/* BANNER ROW */}
                                <div className="secBannerRow">
                                    <div className="secBannerLeft">
                                        <div className="secThumb">
                                            {sec.bannerImg ? (
                                                <img src={`${process.env.REACT_APP_IMAGE_URL}${sec.bannerImg}`} alt="banner" />
                                            ) : (
                                                <span className="text-muted small">No Banner</span>
                                            )}
                                        </div>

                                        <div className="secUrl">
                                            <div className="text-muted small">See All</div>
                                            <div>
                                                {parseInt(sec.seeAll) === 1 && sec.url ? (
                                                    <a
                                                        href={`${process.env.REACT_APP_WEBSITE_URL}${sec.url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {sec.url}
                                                    </a>
                                                ) : (
                                                    <span className="text-muted">Disabled</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        variant="info"
                                        size="sm"
                                        className="btnCompact btnCompactWide"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditBanner(sec, 'section');
                                        }}
                                    >
                                        {[8, 9].includes(Number(sec.secType)) ? "Edit background" : "✏️ Edit Banner"}
                                    </Button>
                                </div>

                                {/* BODY */}
                                <div className="p-3">
                                    <Accordion>
                                        {sortByOrder(sec.mobrow || [], 'orderList').map((row, idx) => (
                                            <Accordion.Item eventKey={idx.toString()} key={row.id}>
                                                <Accordion.Header>
                                                    <div className="secRowHeader">
                                                        <div className="secRowLeft">
                                                            <span>🧩 Row {idx + 1}</span>
                                                            <span className="text-muted">| Columns: {[8,9].includes(Number(sec.secType)) ? Math.max(1,(row.mobdata || []).filter(item => Number(item.status) === 1).length) : row.noofcols}{[8,9].includes(Number(sec.secType)) ? " (automatic)" : ""}</span>
                                                            <span className="badge bg-secondary ms-2">Order: {getNum(row.orderList, idx + 1)}</span>
                                                            <span className={`secDot ${row.status === 1 ? 'secDotActive' : 'secDotInactive'}`} />
                                                            <span className={row.status === 1 ? 'text-success' : 'text-danger'}>
                                                                {row.status === 1 ? 'Active' : 'Inactive'}
                                                            </span>
                                                            {row.title ? <span className="text-muted">| {row.title}</span> : null}
                                                        </div>
                                                        <div className="secRowRight">
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                className="me-2 btnCompact"
                                                                title="Move up"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveRow(sec, row.id, 'up');
                                                                }}
                                                            >
                                                                ↑
                                                            </Button>
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                className="me-2 btnCompact"
                                                                title="Move down"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    moveRow(sec, row.id, 'down');
                                                                }}
                                                            >
                                                                ↓
                                                            </Button>

                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-2 btnCompact"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAddItem(row.id, [8, 9].includes(Number(sec.secType)));
                                                                }}
                                                            >
                                                                + Add Item
                                                            </Button>

                                                            <Button
                                                                variant={row.status === 1 ? 'outline-danger' : 'outline-success'}
                                                                size="sm"
                                                                className="btnCompact"
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // ✅ keep accordion icon working
                                                                    handleStatusToggle('row', row.id, row.status);
                                                                }}
                                                            >
                                                                {row.status === 1 ? 'Disable' : 'Enable'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Accordion.Header>

                                                <Accordion.Body>
                                                    {[1, 2, 3, 4, 6, 8, 9].includes(Number(sec.secType)) ? (
                                                        row.mobdata && row.mobdata.length > 0 ? (
                                                            <>
                                                                {[4].includes(sec.secType) ? (
                                                                    <Row className="mb-3 align-items-center">
                                                                        <Col md={8}>
                                                                            <div className="d-flex align-items-center">
                                                                                <img
                                                                                    src={`${process.env.REACT_APP_IMAGE_URL}${row.bannerImg}`}
                                                                                    alt="Row Banner"
                                                                                    className="img-fluid rounded"
                                                                                    style={{ maxHeight: '120px', marginRight: 10 }}
                                                                                />
                                                                                <div>
                                                                                    <div className="text-muted small">Row Banner URL</div>
                                                                                    <a
                                                                                        href={`${process.env.REACT_APP_WEBSITE_URL}${row.url}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                    >
                                                                                        {row.url}
                                                                                    </a>
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={4} className="text-end">
                                                                            <Button
                                                                                variant="info"
                                                                                size="sm"
                                                                                className="btnCompact btnCompactWide"
                                                                                onClick={() => handleEditBanner(row, 'row')}
                                                                            >
                                                                                ✏️ Edit Row Banner
                                                                            </Button>
                                                                        </Col>
                                                                    </Row>
                                                                ) : null}

                                                                <Row>
                                                                    {sortByOrder(row.mobdata || [], 'orderList').map((item, i) => (
                                                                        <Col md={4} sm={4} xs={6} key={i} className="mb-3">
                                                                            <Card className="h-100 shadow-sm border position-relative">
                                                                                {(item.valueData || item.bannerImg) ? (
                                                                                    <img
                                                                                        src={`${process.env.REACT_APP_IMAGE_URL}${item.valueData || item.bannerImg}`}
                                                                                        alt={`Banner ${i + 1}`}
                                                                                        className="img-fluid"
                                                                                        style={{ height: 140, objectFit: 'cover' }}
                                                                                    />
                                                                                ) : null}

                                                                                <div
                                                                                    className={`position-absolute top-0 start-0 ${item.status === 1 ? 'bg-success' : 'bg-danger'
                                                                                        } text-white px-2 py-1 small rounded-end`}
                                                                                >
                                                                                    {item.status === 1 ? 'Active' : 'Inactive'}
                                                                                </div>

                                                                                <span
                                                                                    className="position-absolute top-0 end-0 px-2 py-1 badge bg-info text-white"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onClick={() => handleItemEdit(item)}
                                                                                >
                                                                                    ✏️ Edit
                                                                                </span>

                                                                                <div className="position-absolute bottom-0 start-0 d-flex align-items-center gap-1 p-1">
                                                                                    <span className="badge bg-secondary">#{getNum(item.orderList, i + 1)}</span>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="light"
                                                                                        className="py-0 px-1"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            moveItem(row, item.id, 'up');
                                                                                        }}
                                                                                    >
                                                                                        ↑
                                                                                    </Button>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="light"
                                                                                        className="py-0 px-1"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            moveItem(row, item.id, 'down');
                                                                                        }}
                                                                                    >
                                                                                        ↓
                                                                                    </Button>
                                                                                </div>

                                                                                <Card.Body>
                                                                                    {item.actionIdType === 1 ? (
                                                                                        getProductDetails(item)
                                                                                    ) : (
                                                                                        <div className="small">
                                                                                            {item.title ? (
                                                                                                <div>
                                                                                                    <b>Title:</b> {item.title}
                                                                                                </div>
                                                                                            ) : null}
                                                                                            {item.slug ? (
                                                                                                <div>
                                                                                                    <b>Slug:</b> {item.slug}
                                                                                                </div>
                                                                                            ) : null}
                                                                                            {item.url ? (
                                                                                                <div>
                                                                                                    <b>URL:</b>{' '}
                                                                                                    <a
                                                                                                        href={`${process.env.REACT_APP_WEBSITE_URL}${item.url}`}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                    >
                                                                                                        {item.url}
                                                                                                    </a>
                                                                                                </div>
                                                                                            ) : null}
                                                                                        </div>
                                                                                    )}
                                                                                </Card.Body>

                                                                                <div className="position-absolute bottom-0 end-0">
                                                                                    <Button
                                                                                        size="sm"
                                                                                        className={`prodeactBtn rounded-start ${item.status === 1 ? 'btn-danger' : 'btn-success'
                                                                                            }`}
                                                                                        onClick={() => handleStatusToggle('mobdata', item.id, item.status)}
                                                                                    >
                                                                                        {item.status === 1 ? 'Disable' : 'Enable'}
                                                                                    </Button>
                                                                                </div>
                                                                            </Card>
                                                                        </Col>
                                                                    ))}
                                                                </Row>
                                                            </>
                                                        ) : (
                                                            <div className="text-center text-muted py-3">🚫 No records found for this row.</div>
                                                        )
                                                    ) : row.mobdata && row.mobdata.length > 0 ? (
                                                        <Row>
                                                            {sortByOrder(row.mobdata || [], 'orderList').map((product, pi) => (
                                                                <Col key={product.id} md={4} sm={4} xs={6} className="mb-4">
                                                                    <Card className="h-100 shadow-sm border position-relative">
                                                                        <div
                                                                            className={`position-absolute top-0 start-0 ${product.status === 1 ? 'bg-success' : 'bg-danger'
                                                                                } text-white px-2 py-1 small rounded-end`}
                                                                        >
                                                                            {product.status === 1 ? 'Active' : 'Inactive'}
                                                                        </div>

                                                                        <span
                                                                            className="position-absolute top-0 end-0 px-2 py-1 badge bg-info text-white"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={() => handleItemEdit(product)}
                                                                        >
                                                                            ✏️ Edit
                                                                        </span>

                                                                        <div className="position-absolute bottom-0 start-0 d-flex align-items-center gap-1 p-1">
                                                                            <span className="badge bg-secondary">#{getNum(product.orderList, pi + 1)}</span>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="light"
                                                                                className="py-0 px-1"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    moveItem(row, product.id, 'up');
                                                                                }}
                                                                            >
                                                                                ↑
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="light"
                                                                                className="py-0 px-1"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    moveItem(row, product.id, 'down');
                                                                                }}
                                                                            >
                                                                                ↓
                                                                            </Button>
                                                                        </div>

                                                                        <Card.Body className="d-flex flex-column align-items-center text-center">
                                                                            {getProductDetails(product)}
                                                                        </Card.Body>

                                                                        <div className="position-absolute bottom-0 end-0">
                                                                            <Button
                                                                                className={`prodeactBtn rounded-start ${product.status === 1 ? 'btn-danger' : 'btn-success'
                                                                                    }`}
                                                                                size="sm"
                                                                                onClick={() => handleStatusToggle('mobdata', product.id, product.status)}
                                                                            >
                                                                                {product.status === 1 ? 'Disable' : 'Enable'}
                                                                            </Button>
                                                                        </div>
                                                                    </Card>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    ) : (
                                                        <div className="text-center text-muted py-3">🚫 No records found for this row.</div>
                                                    )}
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                )
            }
            )}

            {
                showForm && (
                    <SectionForm
                        section={editingSection}
                        themes={activeThemes}
                        defaultThemeId={themeId}
                        defaultSectionFor={sectionFor}
                        onClose={handleFormClose}
                    />
                )
            }

            {
                showEditModal && (
                    <EditItemModal
                        show={showEditModal}
                        onClose={handleModalClose}
                        item={selectedItem}
                        onSave={handleItemSave}
                        saving={saving}
                    />
                )
            }

            {
                showBannerEditModal && (
                    <BannerEditModal
                        show={showBannerEditModal}
                        onClose={() => setShowBannerEditModal(false)}
                        section={editingSection}
                        type={bannerEditType}
                        onSave={async (formData) => {
                            setSaving(true);
                            try {
                                const response = await mobSectionApi.updateSectionData(formData);
                                if (response?.success) {
                                    setShowBannerEditModal(false);
                                    fetchSections();
                                }
                            } catch (err) {
                                console.error('Error updating banner:', err);
                            } finally {
                                setSaving(false);
                            }
                        }}
                        saving={saving}
                    />
                )
            }
        </Container >
    );
}

export default MobileSectionList;


